import type { PackagePricingResult } from '@growthdept/blocks/gift-pricing';
import { calculateGiftPackagePricing } from './packagePricing';
import { supabase } from './supabase';
import type { GiftPackage } from './types';

export interface PricingConversionAudit {
  id: string;
  package_id: string;
  project_id: string;
  from_model: 'legacy_v1' | 'component_margin_v2';
  to_model: 'legacy_v1' | 'component_margin_v2';
  before_input: Record<string, unknown>;
  after_input: Record<string, unknown>;
  before_totals: Record<string, unknown>;
  after_totals: Record<string, unknown>;
  converted_at: string;
  reverted_at: string | null;
}

type MarginSource = Pick<GiftPackage, 'margin_pct' | 'margin_high_pct'>;

export function serviceMarginDefaults(
  pkg: Partial<MarginSource>
): { handling: number; transport: number } {
  const margin = pkg.margin_high_pct ?? pkg.margin_pct ?? 30;
  return { handling: margin, transport: margin };
}

export function buildPricingConversionPreview(
  pkg: GiftPackage,
  proposedHandling?: number,
  proposedTransport?: number
): { before: PackagePricingResult; after: PackagePricingResult } {
  const defaults = serviceMarginDefaults(pkg);
  return {
    before: calculateGiftPackagePricing({ ...pkg, pricing_model_version: 'legacy_v1' }),
    after: calculateGiftPackagePricing({
      ...pkg,
      pricing_model_version: 'component_margin_v2',
      handling_margin_pct: proposedHandling ?? defaults.handling,
      transport_margin_pct: proposedTransport ?? defaults.transport,
    }),
  };
}

function rpcPackage(data: unknown, action: string): GiftPackage {
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error(`${action} gaf geen bijgewerkt pakket terug`);
  return row as GiftPackage;
}

export async function convertPackagePricing(
  packageId: string,
  handlingMarginPct: number,
  transportMarginPct: number,
  beforeTotals: Record<string, unknown>,
  afterTotals: Record<string, unknown>
): Promise<GiftPackage> {
  const { data, error } = await supabase.rpc('convert_gift_package_pricing_v2', {
    p_package_id: packageId,
    p_handling_margin_pct: handlingMarginPct,
    p_transport_margin_pct: transportMarginPct,
    p_before_totals: beforeTotals,
    p_after_totals: afterTotals,
  });
  if (error) throw error;
  return rpcPackage(data, 'Conversie');
}

export async function revertPackagePricing(packageId: string): Promise<GiftPackage> {
  const { data, error } = await supabase.rpc('revert_gift_package_pricing_v2', {
    p_package_id: packageId,
  });
  if (error) throw error;
  return rpcPackage(data, 'Herstel');
}

export async function fetchLatestPricingConversion(
  packageId: string
): Promise<PricingConversionAudit | null> {
  const { data, error } = await supabase
    .from('gift_package_pricing_conversions')
    .select('*')
    .eq('package_id', packageId)
    .order('converted_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as PricingConversionAudit | null) ?? null;
}
